import React, { useState, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    InputAdornment,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    alpha,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import {
    BaseballRecruitProfile,
    scrapeAllProfiles,
    refreshCollegeStats,
    searchBrSummerStats,
    getScrapeJobStatus,
} from 'api/BaseballRecruits';
import BaseballCardGrid from './BaseballCardGrid';
import BaseballGameLog from './BaseballGameLog';
import BaseballScoutingReport from './BaseballScoutingReport';

interface BaseballStatsViewerProps {
    open: boolean;
    onClose: () => void;
    profiles: BaseballRecruitProfile[];
    groupUuid?: string;
    groupName?: string;
    groupLogo?: string;
    onRefresh?: () => Promise<void> | void;
}

type SortDirection = 'asc' | 'desc';

const BATTING_COLS = [
    'GP',
    'AVG',
    'AB',
    'R',
    'H',
    'RBI',
    '2B',
    '3B',
    'HR',
    'BB',
    'K',
    'SB',
    'OBP',
    'SLG',
    'OPS',
] as const;

const PITCHING_COLS = [
    'ERA',
    'W',
    'L',
    'APP',
    'GS',
    'SV',
    'IP',
    'H',
    'ER',
    'BB',
    'K',
    'WHIP',
] as const;

const HIGHER_IS_WORSE = new Set(['K', 'L', 'ER', 'WHIP', 'ERA']);

function parseStatValue(val: string | undefined): number {
    if (!val || val === '-') return -Infinity;
    const num = parseFloat(val);
    return isNaN(num) ? -Infinity : num;
}

function sortRows<T>(
    rows: T[],
    getStatValue: (row: T, col: string) => string | undefined,
    getPlayerName: (row: T) => string,
    sortCol: string | null,
    sortDir: SortDirection,
): T[] {
    if (!sortCol) return rows;
    return [...rows].sort((a, b) => {
        if (sortCol === 'Player') {
            const nameA = getPlayerName(a).toLowerCase();
            const nameB = getPlayerName(b).toLowerCase();
            return sortDir === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        if (sortCol === 'Season') {
            const valA = (getStatValue(a, '_season') || '').toLowerCase();
            const valB = (getStatValue(b, '_season') || '').toLowerCase();
            return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        const valA = parseStatValue(getStatValue(a, sortCol));
        const valB = parseStatValue(getStatValue(b, sortCol));
        return sortDir === 'asc' ? valA - valB : valB - valA;
    });
}

const stickyCell = (theme: any) => ({
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    position: 'sticky' as const,
    left: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
});

const headerCell = (theme: any) => ({
    fontWeight: 700,
    fontSize: '0.7rem',
    position: 'sticky' as const,
    left: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 2,
});

const statHeaderCell = {
    fontWeight: 700,
    fontSize: '0.7rem',
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
};

const statCell = {
    fontSize: '0.75rem',
    textAlign: 'center' as const,
};

interface SortableHeaderProps {
    label: string;
    column: string;
    activeColumn: string | null;
    direction: SortDirection;
    onSort: (col: string) => void;
    sx?: object;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
    label,
    column,
    activeColumn,
    direction,
    onSort,
    sx,
}) => (
    <TableCell sx={{ ...statHeaderCell, ...sx }}>
        <TableSortLabel
            active={activeColumn === column}
            direction={activeColumn === column ? direction : 'desc'}
            onClick={() => onSort(column)}
            sx={{ '& .MuiTableSortLabel-icon': { fontSize: '0.85rem' } }}
        >
            {label}
        </TableSortLabel>
    </TableCell>
);

const BaseballStatsViewer: React.FC<BaseballStatsViewerProps> = ({
    open,
    onClose,
    profiles,
    groupUuid,
    groupName,
    groupLogo,
    onRefresh,
}) => {
    const theme = useTheme();
    const [tab, setTab] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState<string>('');

    const [careerBattingSort, setCareerBattingSort] = useState<string | null>(null);
    const [careerBattingDir, setCareerBattingDir] = useState<SortDirection>('desc');
    const [careerPitchingSort, setCareerPitchingSort] = useState<string | null>(null);
    const [careerPitchingDir, setCareerPitchingDir] = useState<SortDirection>('desc');

    const [seasonBattingSort, setSeasonBattingSort] = useState<string | null>(null);
    const [seasonBattingDir, setSeasonBattingDir] = useState<SortDirection>('desc');
    const [seasonPitchingSort, setSeasonPitchingSort] = useState<string | null>(null);
    const [seasonPitchingDir, setSeasonPitchingDir] = useState<SortDirection>('desc');

    const [filterText, setFilterText] = useState('');
    const [viewLevels, setViewLevels] = useState<Set<string> | null>(null);

    const profilesWithData = profiles.filter((p) => p.scraped_data);
    const profilesWithCollegeData = profiles.filter((p) => p.college_scraped_data);
    const profilesWithSummerData = profiles.filter((p) => p.summer_league_data);
    const profilesWithAnyData = profiles.filter(
        (p) => p.scraped_data || p.college_scraped_data || p.summer_league_data,
    );

    const getPlayerName = useCallback((p: BaseballRecruitProfile) => {
        if (p.individual?.name) return p.individual.name;
        if (p.college_scraped_data?.player?.first_name)
            return `${p.college_scraped_data.player.first_name} ${p.college_scraped_data.player.last_name}`;
        if (p.player_bio) return `${p.player_bio.first_name} ${p.player_bio.last_name}`;
        return 'Unknown';
    }, []);

    const selectedProfile = selectedPlayer
        ? profilesWithAnyData.find((p) => p.uuid === selectedPlayer)
        : null;

    const getGameLogs = (profile: BaseballRecruitProfile) => {
        const data = profile.scraped_data;
        if (!data) return [];
        return (
            data.seasons_data?.flatMap((s) =>
                s.game_logs?.length ? s.game_logs : s.schedule || [],
            ) || []
        );
    };

    const getCollegeGameLogs = (profile: BaseballRecruitProfile) => {
        const data = profile.college_scraped_data;
        if (!data) return [];
        return data.seasons_data?.flatMap((s) => (s.game_logs?.length ? s.game_logs : [])) || [];
    };

    const getSummerGameLogs = (profile: BaseballRecruitProfile) => {
        const data = profile.summer_league_data;
        if (!data) return [];
        return data.seasons_data?.flatMap((s) => (s.game_logs?.length ? s.game_logs : [])) || [];
    };

    const matchesFilter = useCallback(
        (name: string) => {
            if (!filterText) return true;
            return name.toLowerCase().includes(filterText.toLowerCase());
        },
        [filterText],
    );

    const handleSort = (
        col: string,
        currentCol: string | null,
        currentDir: SortDirection,
        setCol: (c: string | null) => void,
        setDir: (d: SortDirection) => void,
    ) => {
        if (currentCol === col) {
            if (currentDir === 'desc') setDir('asc');
            else {
                setCol(null);
                setDir('desc');
            }
        } else {
            setCol(col);
            const defaultDir =
                col === 'Player' || col === 'Season' || HIGHER_IS_WORSE.has(col) ? 'asc' : 'desc';
            setDir(defaultDir);
        }
    };

    // Career batting rows
    const careerBattingRows = useMemo(() => {
        const rows = profilesWithData.filter(
            (p) => p.scraped_data?.career_totals?.batting && matchesFilter(getPlayerName(p)),
        );
        return sortRows(
            rows,
            (p, col) => p.scraped_data?.career_totals?.batting?.[col],
            getPlayerName,
            careerBattingSort,
            careerBattingDir,
        );
    }, [profilesWithData, careerBattingSort, careerBattingDir, getPlayerName, matchesFilter]);

    // Career pitching rows
    const careerPitchingRows = useMemo(() => {
        const rows = profilesWithData.filter(
            (p) => p.scraped_data?.career_totals?.pitching && matchesFilter(getPlayerName(p)),
        );
        return sortRows(
            rows,
            (p, col) => p.scraped_data?.career_totals?.pitching?.[col],
            getPlayerName,
            careerPitchingSort,
            careerPitchingDir,
        );
    }, [profilesWithData, careerPitchingSort, careerPitchingDir, getPlayerName, matchesFilter]);

    // Season batting rows
    type SeasonRow = { profile: BaseballRecruitProfile; seasonIdx: number };
    const seasonBattingRows = useMemo(() => {
        const rows: SeasonRow[] = [];
        profilesWithData.forEach((profile) => {
            if (!matchesFilter(getPlayerName(profile))) return;
            profile.scraped_data?.seasons_data?.forEach((season, si) => {
                if (season.batting) rows.push({ profile, seasonIdx: si });
            });
        });
        return sortRows(
            rows,
            (r, col) => {
                const season = r.profile.scraped_data?.seasons_data?.[r.seasonIdx];
                if (col === '_season') return `${season?.year} ${season?.team_level}`;
                return season?.batting?.[col];
            },
            (r) => getPlayerName(r.profile),
            seasonBattingSort,
            seasonBattingDir,
        );
    }, [profilesWithData, seasonBattingSort, seasonBattingDir, getPlayerName, matchesFilter]);

    // Season pitching rows
    const seasonPitchingRows = useMemo(() => {
        const rows: SeasonRow[] = [];
        profilesWithData.forEach((profile) => {
            if (!matchesFilter(getPlayerName(profile))) return;
            profile.scraped_data?.seasons_data?.forEach((season, si) => {
                if (season.pitching) rows.push({ profile, seasonIdx: si });
            });
        });
        return sortRows(
            rows,
            (r, col) => {
                const season = r.profile.scraped_data?.seasons_data?.[r.seasonIdx];
                if (col === '_season') return `${season?.year} ${season?.team_level}`;
                return season?.pitching?.[col];
            },
            (r) => getPlayerName(r.profile),
            seasonPitchingSort,
            seasonPitchingDir,
        );
    }, [profilesWithData, seasonPitchingSort, seasonPitchingDir, getPlayerName, matchesFilter]);

    // College career batting rows
    const collegeCareerBattingRows = useMemo(() => {
        const rows = profilesWithCollegeData.filter(
            (p) =>
                p.college_scraped_data?.career_totals?.batting && matchesFilter(getPlayerName(p)),
        );
        return sortRows(
            rows,
            (p, col) => p.college_scraped_data?.career_totals?.batting?.[col],
            getPlayerName,
            careerBattingSort,
            careerBattingDir,
        );
    }, [
        profilesWithCollegeData,
        careerBattingSort,
        careerBattingDir,
        getPlayerName,
        matchesFilter,
    ]);

    // College career pitching rows
    const collegeCareerPitchingRows = useMemo(() => {
        const rows = profilesWithCollegeData.filter(
            (p) =>
                p.college_scraped_data?.career_totals?.pitching && matchesFilter(getPlayerName(p)),
        );
        return sortRows(
            rows,
            (p, col) => p.college_scraped_data?.career_totals?.pitching?.[col],
            getPlayerName,
            careerPitchingSort,
            careerPitchingDir,
        );
    }, [
        profilesWithCollegeData,
        careerPitchingSort,
        careerPitchingDir,
        getPlayerName,
        matchesFilter,
    ]);

    type CollegeSeasonRow = { profile: BaseballRecruitProfile; seasonIdx: number };

    // College season batting rows
    const collegeSeasonBattingRows = useMemo(() => {
        const rows: CollegeSeasonRow[] = [];
        profilesWithCollegeData.forEach((profile) => {
            if (!matchesFilter(getPlayerName(profile))) return;
            profile.college_scraped_data?.seasons_data?.forEach((season, si) => {
                if (season.batting) rows.push({ profile, seasonIdx: si });
            });
        });
        return sortRows(
            rows,
            (r, col) => {
                const season = r.profile.college_scraped_data?.seasons_data?.[r.seasonIdx];
                if (col === '_season') return `${season?.year} ${season?.team_level}`;
                return season?.batting?.[col];
            },
            (r) => getPlayerName(r.profile),
            seasonBattingSort,
            seasonBattingDir,
        );
    }, [
        profilesWithCollegeData,
        seasonBattingSort,
        seasonBattingDir,
        getPlayerName,
        matchesFilter,
    ]);

    // College season pitching rows
    const collegeSeasonPitchingRows = useMemo(() => {
        const rows: CollegeSeasonRow[] = [];
        profilesWithCollegeData.forEach((profile) => {
            if (!matchesFilter(getPlayerName(profile))) return;
            profile.college_scraped_data?.seasons_data?.forEach((season, si) => {
                if (season.pitching) rows.push({ profile, seasonIdx: si });
            });
        });
        return sortRows(
            rows,
            (r, col) => {
                const season = r.profile.college_scraped_data?.seasons_data?.[r.seasonIdx];
                if (col === '_season') return `${season?.year} ${season?.team_level}`;
                return season?.pitching?.[col];
            },
            (r) => getPlayerName(r.profile),
            seasonPitchingSort,
            seasonPitchingDir,
        );
    }, [
        profilesWithCollegeData,
        seasonPitchingSort,
        seasonPitchingDir,
        getPlayerName,
        matchesFilter,
    ]);

    // Summer season batting rows
    const summerSeasonBattingRows = useMemo(() => {
        const rows: CollegeSeasonRow[] = [];
        profilesWithSummerData.forEach((profile) => {
            if (!matchesFilter(getPlayerName(profile))) return;
            profile.summer_league_data?.seasons_data?.forEach((season, si) => {
                if (season.batting) rows.push({ profile, seasonIdx: si });
            });
        });
        return sortRows(
            rows,
            (r, col) => {
                const season = r.profile.summer_league_data?.seasons_data?.[r.seasonIdx];
                if (col === '_season') return `${season?.year} ${season?.team_level}`;
                return season?.batting?.[col];
            },
            (r) => getPlayerName(r.profile),
            seasonBattingSort,
            seasonBattingDir,
        );
    }, [profilesWithSummerData, seasonBattingSort, seasonBattingDir, getPlayerName, matchesFilter]);

    // Summer season pitching rows
    const summerSeasonPitchingRows = useMemo(() => {
        const rows: CollegeSeasonRow[] = [];
        profilesWithSummerData.forEach((profile) => {
            if (!matchesFilter(getPlayerName(profile))) return;
            profile.summer_league_data?.seasons_data?.forEach((season, si) => {
                if (season.pitching) rows.push({ profile, seasonIdx: si });
            });
        });
        return sortRows(
            rows,
            (r, col) => {
                const season = r.profile.summer_league_data?.seasons_data?.[r.seasonIdx];
                if (col === '_season') return `${season?.year} ${season?.team_level}`;
                return season?.pitching?.[col];
            },
            (r) => getPlayerName(r.profile),
            seasonPitchingSort,
            seasonPitchingDir,
        );
    }, [
        profilesWithSummerData,
        seasonPitchingSort,
        seasonPitchingDir,
        getPlayerName,
        matchesFilter,
    ]);

    // Summer career batting rows
    const summerCareerBattingRows = useMemo(() => {
        const rows = profilesWithSummerData.filter(
            (p) => p.summer_league_data?.career_totals?.batting && matchesFilter(getPlayerName(p)),
        );
        return sortRows(
            rows,
            (p, col) => p.summer_league_data?.career_totals?.batting?.[col],
            getPlayerName,
            careerBattingSort,
            careerBattingDir,
        );
    }, [profilesWithSummerData, careerBattingSort, careerBattingDir, getPlayerName, matchesFilter]);

    // Summer career pitching rows
    const summerCareerPitchingRows = useMemo(() => {
        const rows = profilesWithSummerData.filter(
            (p) => p.summer_league_data?.career_totals?.pitching && matchesFilter(getPlayerName(p)),
        );
        return sortRows(
            rows,
            (p, col) => p.summer_league_data?.career_totals?.pitching?.[col],
            getPlayerName,
            careerPitchingSort,
            careerPitchingDir,
        );
    }, [
        profilesWithSummerData,
        careerPitchingSort,
        careerPitchingDir,
        getPlayerName,
        matchesFilter,
    ]);

    const hasCollegeData = profilesWithCollegeData.length > 0;
    const hasSummerData = profilesWithSummerData.length > 0;
    const hasHsData = profilesWithData.length > 0;
    const dataLevelCount = [hasCollegeData, hasSummerData, hasHsData].filter(Boolean).length;

    const defaultLevels = useMemo(() => {
        const s = new Set<string>();
        if (hasCollegeData) s.add('college');
        if (hasSummerData) s.add('summer');
        if (hasHsData) s.add('hs');
        return s;
    }, [hasCollegeData, hasSummerData, hasHsData]);

    const visible = viewLevels ?? defaultLevels;
    const showCollege = visible.has('college');
    const showSummer = visible.has('summer');
    const showHs = visible.has('hs');

    const handleToggleLevel = (_: unknown, newLevels: string[]) => {
        if (newLevels.length === 0) return;
        setViewLevels(new Set(newLevels));
    };

    const viewToggleBar = dataLevelCount >= 2 && (
        <ToggleButtonGroup size="small" value={Array.from(visible)} onChange={handleToggleLevel}>
            {hasCollegeData && (
                <ToggleButton
                    value="college"
                    sx={{
                        '&.Mui-selected': {
                            backgroundColor: alpha('#7b1fa2', 0.12),
                            color: '#7b1fa2',
                        },
                    }}
                >
                    College
                </ToggleButton>
            )}
            {hasSummerData && (
                <ToggleButton
                    value="summer"
                    sx={{
                        '&.Mui-selected': {
                            backgroundColor: alpha('#00897b', 0.12),
                            color: '#00897b',
                        },
                    }}
                >
                    Summer
                </ToggleButton>
            )}
            {hasHsData && (
                <ToggleButton
                    value="hs"
                    sx={{
                        '&.Mui-selected': {
                            backgroundColor: alpha('#1565c0', 0.12),
                            color: '#1565c0',
                        },
                    }}
                >
                    HS
                </ToggleButton>
            )}
        </ToggleButtonGroup>
    );

    const filterBar = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                pt: 2,
                pb: 1,
            }}
        >
            <TextField
                size="small"
                placeholder="Search players..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ minWidth: 220 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                }}
            />
            {viewToggleBar}
            {filterText && (
                <Chip
                    label={`Showing: "${filterText}"`}
                    size="small"
                    onDelete={() => setFilterText('')}
                    color="primary"
                    variant="outlined"
                />
            )}
        </Box>
    );

    const sectionHeader = (label: string, color: string) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                mb: 1,
                px: 1,
                py: 0.5,
                borderLeft: `3px solid ${color}`,
                backgroundColor: `${color}10`,
            }}
        >
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
                {label}
            </Typography>
        </Box>
    );

    const renderHighlight = (value: string | undefined, col: string, sortCol: string | null) => {
        const display = value || '-';
        const isSorted = sortCol === col;
        return (
            <Box
                component="span"
                sx={{
                    fontWeight: isSorted ? 700 : 400,
                    color: isSorted ? 'primary.main' : 'inherit',
                }}
            >
                {display}
            </Box>
        );
    };

    const handleRefreshAll = useCallback(async () => {
        if (!groupUuid || refreshing) return;
        setRefreshing(true);
        try {
            const hasCollegeUrls = profiles.some((p) => p.college_stats_url);
            const hasSummerUrls = profiles.some((p) => p.summer_league_url);

            const [hsResult] = await Promise.allSettled([
                scrapeAllProfiles(groupUuid),
                ...(hasCollegeUrls ? [refreshCollegeStats(groupUuid)] : []),
                ...(hasSummerUrls ? [searchBrSummerStats(groupUuid)] : []),
            ]);

            // College + summer are synchronous; reload now for partial update
            if (onRefresh) await onRefresh();

            // HS scrape is async (queued jobs) -- poll until done then reload again
            if (hsResult.status === 'fulfilled' && hsResult.value.data.job_ids?.length) {
                const jobIds = hsResult.value.data.job_ids;
                const POLL_INTERVAL = 5000;
                const MAX_POLL_TIME = 120000;
                const start = Date.now();
                const pending = new Set(jobIds);

                while (pending.size > 0 && Date.now() - start < MAX_POLL_TIME) {
                    await new Promise((r) => setTimeout(r, POLL_INTERVAL));
                    for (const id of [...pending]) {
                        try {
                            const { data } = await getScrapeJobStatus(id);
                            if (data.status === 'completed' || data.status === 'failed') {
                                pending.delete(id);
                            }
                        } catch {
                            pending.delete(id);
                        }
                    }
                }

                if (onRefresh) await onRefresh();
            }
        } finally {
            setRefreshing(false);
        }
    }, [groupUuid, refreshing, onRefresh, profiles]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Baseball Recruiting Stats
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {groupUuid && (
                        <Tooltip title="Re-scrape all data (HS, College, Summer)">
                            <span>
                                <IconButton
                                    onClick={handleRefreshAll}
                                    size="small"
                                    disabled={refreshing}
                                >
                                    {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        px: 2,
                    }}
                >
                    <Tab label="Cards View" />
                    <Tab label="Season Stats" />
                    <Tab label="Game Logs" />
                    <Tab label="Career Totals" />
                    <Tab label="Scouting" />
                </Tabs>

                {/* Cards View */}
                {tab === 0 && <BaseballCardGrid profiles={profiles} />}

                {/* Season Stats */}
                {tab === 1 && (
                    <Box>
                        {filterBar}
                        <Box sx={{ px: 2, pb: 2 }}>
                            {/* College Season Stats */}
                            {showCollege &&
                                hasCollegeData &&
                                collegeSeasonBattingRows.length > 0 && (
                                    <>
                                        {sectionHeader('College Season Batting', '#8e24aa')}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {collegeSeasonBattingRows.length} row
                                                {collegeSeasonBattingRows.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                        <TableContainer
                                            sx={{
                                                maxHeight: 400,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <SortableHeader
                                                            label="Player"
                                                            column="Player"
                                                            activeColumn={seasonBattingSort}
                                                            direction={seasonBattingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonBattingSort,
                                                                    seasonBattingDir,
                                                                    setSeasonBattingSort,
                                                                    setSeasonBattingDir,
                                                                )
                                                            }
                                                            sx={headerCell(theme)}
                                                        />
                                                        <SortableHeader
                                                            label="Season"
                                                            column="Season"
                                                            activeColumn={seasonBattingSort}
                                                            direction={seasonBattingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonBattingSort,
                                                                    seasonBattingDir,
                                                                    setSeasonBattingSort,
                                                                    setSeasonBattingDir,
                                                                )
                                                            }
                                                        />
                                                        {BATTING_COLS.map((col) => (
                                                            <SortableHeader
                                                                key={col}
                                                                label={col}
                                                                column={col}
                                                                activeColumn={seasonBattingSort}
                                                                direction={seasonBattingDir}
                                                                onSort={(c) =>
                                                                    handleSort(
                                                                        c,
                                                                        seasonBattingSort,
                                                                        seasonBattingDir,
                                                                        setSeasonBattingSort,
                                                                        setSeasonBattingDir,
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {collegeSeasonBattingRows.map((row) => {
                                                        const season =
                                                            row.profile.college_scraped_data
                                                                ?.seasons_data?.[row.seasonIdx];
                                                        return (
                                                            <TableRow
                                                                key={`col-${row.profile.uuid}-${row.seasonIdx}`}
                                                                hover
                                                            >
                                                                <TableCell sx={stickyCell(theme)}>
                                                                    {getPlayerName(row.profile)}
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontSize: '0.7rem',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {season?.year}{' '}
                                                                    {season?.team_level}
                                                                </TableCell>
                                                                {BATTING_COLS.map((col) => (
                                                                    <TableCell
                                                                        key={col}
                                                                        sx={statCell}
                                                                    >
                                                                        {renderHighlight(
                                                                            season?.batting?.[col],
                                                                            col,
                                                                            seasonBattingSort,
                                                                        )}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                            {showCollege &&
                                hasCollegeData &&
                                collegeSeasonPitchingRows.length > 0 && (
                                    <>
                                        {sectionHeader('College Season Pitching', '#8e24aa')}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {collegeSeasonPitchingRows.length} row
                                                {collegeSeasonPitchingRows.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                        <TableContainer
                                            sx={{
                                                maxHeight: 400,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <SortableHeader
                                                            label="Player"
                                                            column="Player"
                                                            activeColumn={seasonPitchingSort}
                                                            direction={seasonPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonPitchingSort,
                                                                    seasonPitchingDir,
                                                                    setSeasonPitchingSort,
                                                                    setSeasonPitchingDir,
                                                                )
                                                            }
                                                            sx={headerCell(theme)}
                                                        />
                                                        <SortableHeader
                                                            label="Season"
                                                            column="Season"
                                                            activeColumn={seasonPitchingSort}
                                                            direction={seasonPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonPitchingSort,
                                                                    seasonPitchingDir,
                                                                    setSeasonPitchingSort,
                                                                    setSeasonPitchingDir,
                                                                )
                                                            }
                                                        />
                                                        {PITCHING_COLS.map((col) => (
                                                            <SortableHeader
                                                                key={col}
                                                                label={col}
                                                                column={col}
                                                                activeColumn={seasonPitchingSort}
                                                                direction={seasonPitchingDir}
                                                                onSort={(c) =>
                                                                    handleSort(
                                                                        c,
                                                                        seasonPitchingSort,
                                                                        seasonPitchingDir,
                                                                        setSeasonPitchingSort,
                                                                        setSeasonPitchingDir,
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {collegeSeasonPitchingRows.map((row) => {
                                                        const season =
                                                            row.profile.college_scraped_data
                                                                ?.seasons_data?.[row.seasonIdx];
                                                        return (
                                                            <TableRow
                                                                key={`col-${row.profile.uuid}-p-${row.seasonIdx}`}
                                                                hover
                                                            >
                                                                <TableCell sx={stickyCell(theme)}>
                                                                    {getPlayerName(row.profile)}
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        fontSize: '0.7rem',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {season?.year}{' '}
                                                                    {season?.team_level}
                                                                </TableCell>
                                                                {PITCHING_COLS.map((col) => (
                                                                    <TableCell
                                                                        key={col}
                                                                        sx={statCell}
                                                                    >
                                                                        {renderHighlight(
                                                                            season?.pitching?.[col],
                                                                            col,
                                                                            seasonPitchingSort,
                                                                        )}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                            {/* Summer League Season Stats */}
                            {showSummer && hasSummerData && summerSeasonBattingRows.length > 0 && (
                                <>
                                    {sectionHeader('Summer League Season Batting', '#00897b')}
                                    <TableContainer
                                        sx={{
                                            maxHeight: 400,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <SortableHeader
                                                        label="Player"
                                                        column="Player"
                                                        activeColumn={seasonBattingSort}
                                                        direction={seasonBattingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonBattingSort,
                                                                seasonBattingDir,
                                                                setSeasonBattingSort,
                                                                setSeasonBattingDir,
                                                            )
                                                        }
                                                        sx={headerCell(theme)}
                                                    />
                                                    <SortableHeader
                                                        label="Season"
                                                        column="Season"
                                                        activeColumn={seasonBattingSort}
                                                        direction={seasonBattingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonBattingSort,
                                                                seasonBattingDir,
                                                                setSeasonBattingSort,
                                                                setSeasonBattingDir,
                                                            )
                                                        }
                                                    />
                                                    {BATTING_COLS.map((col) => (
                                                        <SortableHeader
                                                            key={col}
                                                            label={col}
                                                            column={col}
                                                            activeColumn={seasonBattingSort}
                                                            direction={seasonBattingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonBattingSort,
                                                                    seasonBattingDir,
                                                                    setSeasonBattingSort,
                                                                    setSeasonBattingDir,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summerSeasonBattingRows.map((row) => {
                                                    const season =
                                                        row.profile.summer_league_data
                                                            ?.seasons_data?.[row.seasonIdx];
                                                    return (
                                                        <TableRow
                                                            key={`smr-${row.profile.uuid}-${row.seasonIdx}`}
                                                            hover
                                                        >
                                                            <TableCell sx={stickyCell(theme)}>
                                                                {getPlayerName(row.profile)}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontSize: '0.7rem',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {season?.year
                                                                    ? `${season.year} `
                                                                    : ''}
                                                                {season?.school_name || 'Summer'}
                                                                {season?.league_name
                                                                    ? ` · ${season.league_name}`
                                                                    : ''}
                                                            </TableCell>
                                                            {BATTING_COLS.map((col) => (
                                                                <TableCell key={col} sx={statCell}>
                                                                    {renderHighlight(
                                                                        season?.batting?.[col],
                                                                        col,
                                                                        seasonBattingSort,
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {showSummer && hasSummerData && summerSeasonPitchingRows.length > 0 && (
                                <>
                                    {sectionHeader('Summer League Season Pitching', '#00897b')}
                                    <TableContainer
                                        sx={{
                                            maxHeight: 400,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <SortableHeader
                                                        label="Player"
                                                        column="Player"
                                                        activeColumn={seasonPitchingSort}
                                                        direction={seasonPitchingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonPitchingSort,
                                                                seasonPitchingDir,
                                                                setSeasonPitchingSort,
                                                                setSeasonPitchingDir,
                                                            )
                                                        }
                                                        sx={headerCell(theme)}
                                                    />
                                                    <SortableHeader
                                                        label="Season"
                                                        column="Season"
                                                        activeColumn={seasonPitchingSort}
                                                        direction={seasonPitchingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonPitchingSort,
                                                                seasonPitchingDir,
                                                                setSeasonPitchingSort,
                                                                setSeasonPitchingDir,
                                                            )
                                                        }
                                                    />
                                                    {PITCHING_COLS.map((col) => (
                                                        <SortableHeader
                                                            key={col}
                                                            label={col}
                                                            column={col}
                                                            activeColumn={seasonPitchingSort}
                                                            direction={seasonPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonPitchingSort,
                                                                    seasonPitchingDir,
                                                                    setSeasonPitchingSort,
                                                                    setSeasonPitchingDir,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summerSeasonPitchingRows.map((row) => {
                                                    const season =
                                                        row.profile.summer_league_data
                                                            ?.seasons_data?.[row.seasonIdx];
                                                    return (
                                                        <TableRow
                                                            key={`smr-${row.profile.uuid}-p-${row.seasonIdx}`}
                                                            hover
                                                        >
                                                            <TableCell sx={stickyCell(theme)}>
                                                                {getPlayerName(row.profile)}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontSize: '0.7rem',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {season?.year
                                                                    ? `${season.year} `
                                                                    : ''}
                                                                {season?.school_name || 'Summer'}
                                                                {season?.league_name
                                                                    ? ` · ${season.league_name}`
                                                                    : ''}
                                                            </TableCell>
                                                            {PITCHING_COLS.map((col) => (
                                                                <TableCell key={col} sx={statCell}>
                                                                    {renderHighlight(
                                                                        season?.pitching?.[col],
                                                                        col,
                                                                        seasonPitchingSort,
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {/* High School Season Stats */}
                            {showHs &&
                                (hasCollegeData || hasSummerData) &&
                                seasonBattingRows.length > 0 &&
                                sectionHeader('High School Season Batting', '#1565c0')}
                            {showHs && !hasCollegeData && !hasSummerData && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Season Batting Stats
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {seasonBattingRows.length} row
                                        {seasonBattingRows.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            )}
                            {showHs && seasonBattingRows.length > 0 && (
                                <TableContainer
                                    sx={{
                                        maxHeight: 400,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <SortableHeader
                                                    label="Player"
                                                    column="Player"
                                                    activeColumn={seasonBattingSort}
                                                    direction={seasonBattingDir}
                                                    onSort={(c) =>
                                                        handleSort(
                                                            c,
                                                            seasonBattingSort,
                                                            seasonBattingDir,
                                                            setSeasonBattingSort,
                                                            setSeasonBattingDir,
                                                        )
                                                    }
                                                    sx={headerCell(theme)}
                                                />
                                                <SortableHeader
                                                    label="Season"
                                                    column="Season"
                                                    activeColumn={seasonBattingSort}
                                                    direction={seasonBattingDir}
                                                    onSort={(c) =>
                                                        handleSort(
                                                            c,
                                                            seasonBattingSort,
                                                            seasonBattingDir,
                                                            setSeasonBattingSort,
                                                            setSeasonBattingDir,
                                                        )
                                                    }
                                                />
                                                {BATTING_COLS.map((col) => (
                                                    <SortableHeader
                                                        key={col}
                                                        label={col}
                                                        column={col}
                                                        activeColumn={seasonBattingSort}
                                                        direction={seasonBattingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonBattingSort,
                                                                seasonBattingDir,
                                                                setSeasonBattingSort,
                                                                setSeasonBattingDir,
                                                            )
                                                        }
                                                    />
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {seasonBattingRows.map((row) => {
                                                const season =
                                                    row.profile.scraped_data?.seasons_data?.[
                                                        row.seasonIdx
                                                    ];
                                                return (
                                                    <TableRow
                                                        key={`${row.profile.uuid}-${row.seasonIdx}`}
                                                        hover
                                                    >
                                                        <TableCell sx={stickyCell(theme)}>
                                                            {getPlayerName(row.profile)}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {season?.year} {season?.team_level}
                                                        </TableCell>
                                                        {BATTING_COLS.map((col) => (
                                                            <TableCell key={col} sx={statCell}>
                                                                {renderHighlight(
                                                                    season?.batting?.[col],
                                                                    col,
                                                                    seasonBattingSort,
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {showHs && seasonPitchingRows.length > 0 && (
                                <>
                                    {hasCollegeData || hasSummerData ? (
                                        sectionHeader('High School Season Pitching', '#1565c0')
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mt: 3,
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Season Pitching Stats
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {seasonPitchingRows.length} row
                                                {seasonPitchingRows.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    )}
                                    <TableContainer
                                        sx={{
                                            maxHeight: 400,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <SortableHeader
                                                        label="Player"
                                                        column="Player"
                                                        activeColumn={seasonPitchingSort}
                                                        direction={seasonPitchingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonPitchingSort,
                                                                seasonPitchingDir,
                                                                setSeasonPitchingSort,
                                                                setSeasonPitchingDir,
                                                            )
                                                        }
                                                        sx={headerCell(theme)}
                                                    />
                                                    <SortableHeader
                                                        label="Season"
                                                        column="Season"
                                                        activeColumn={seasonPitchingSort}
                                                        direction={seasonPitchingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                seasonPitchingSort,
                                                                seasonPitchingDir,
                                                                setSeasonPitchingSort,
                                                                setSeasonPitchingDir,
                                                            )
                                                        }
                                                    />
                                                    {PITCHING_COLS.map((col) => (
                                                        <SortableHeader
                                                            key={col}
                                                            label={col}
                                                            column={col}
                                                            activeColumn={seasonPitchingSort}
                                                            direction={seasonPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    seasonPitchingSort,
                                                                    seasonPitchingDir,
                                                                    setSeasonPitchingSort,
                                                                    setSeasonPitchingDir,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {seasonPitchingRows.map((row) => {
                                                    const season =
                                                        row.profile.scraped_data?.seasons_data?.[
                                                            row.seasonIdx
                                                        ];
                                                    return (
                                                        <TableRow
                                                            key={`${row.profile.uuid}-p-${row.seasonIdx}`}
                                                            hover
                                                        >
                                                            <TableCell sx={stickyCell(theme)}>
                                                                {getPlayerName(row.profile)}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontSize: '0.7rem',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {season?.year} {season?.team_level}
                                                            </TableCell>
                                                            {PITCHING_COLS.map((col) => (
                                                                <TableCell key={col} sx={statCell}>
                                                                    {renderHighlight(
                                                                        season?.pitching?.[col],
                                                                        col,
                                                                        seasonPitchingSort,
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Game Logs */}
                {tab === 2 && (
                    <Box sx={{ p: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                mb: 2,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <FormControl size="small" sx={{ minWidth: 250 }}>
                                <InputLabel>Select Player</InputLabel>
                                <Select
                                    value={selectedPlayer}
                                    label="Select Player"
                                    onChange={(e) => setSelectedPlayer(e.target.value)}
                                >
                                    {profilesWithAnyData.map((p) => (
                                        <MenuItem key={p.uuid} value={p.uuid}>
                                            {getPlayerName(p)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {viewToggleBar}
                        </Box>

                        {selectedProfile ? (
                            <>
                                {showCollege && getCollegeGameLogs(selectedProfile).length > 0 && (
                                    <>
                                        {sectionHeader('College Game Log', '#8e24aa')}
                                        <BaseballGameLog
                                            games={getCollegeGameLogs(selectedProfile)}
                                            playerName={`${getPlayerName(
                                                selectedProfile,
                                            )} (College)`}
                                            onRefresh={groupUuid ? handleRefreshAll : undefined}
                                        />
                                    </>
                                )}
                                {showSummer && getSummerGameLogs(selectedProfile).length > 0 && (
                                    <>
                                        {sectionHeader('Summer League Game Log', '#00897b')}
                                        <BaseballGameLog
                                            games={getSummerGameLogs(selectedProfile)}
                                            playerName={`${getPlayerName(
                                                selectedProfile,
                                            )} (Summer)`}
                                            onRefresh={groupUuid ? handleRefreshAll : undefined}
                                        />
                                    </>
                                )}
                                {showHs && getGameLogs(selectedProfile).length > 0 && (
                                    <>
                                        {(hasCollegeData || hasSummerData) &&
                                            sectionHeader('High School Game Log', '#1565c0')}
                                        <BaseballGameLog
                                            games={getGameLogs(selectedProfile)}
                                            playerName={getPlayerName(selectedProfile)}
                                            onRefresh={groupUuid ? handleRefreshAll : undefined}
                                        />
                                    </>
                                )}
                                {getGameLogs(selectedProfile).length === 0 &&
                                    getCollegeGameLogs(selectedProfile).length === 0 &&
                                    getSummerGameLogs(selectedProfile).length === 0 && (
                                        <Typography
                                            color="text.secondary"
                                            sx={{ textAlign: 'center', py: 4 }}
                                        >
                                            No game logs available for this player
                                        </Typography>
                                    )}
                            </>
                        ) : (
                            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                Select a player to view their game-by-game stats
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Career Totals */}
                {tab === 3 && (
                    <Box>
                        {filterBar}
                        <Box sx={{ px: 2, pb: 2 }}>
                            {/* College Career Totals */}
                            {showCollege &&
                                hasCollegeData &&
                                collegeCareerBattingRows.length > 0 && (
                                    <>
                                        {sectionHeader('College Career Batting', '#8e24aa')}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {collegeCareerBattingRows.length} player
                                                {collegeCareerBattingRows.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                        <TableContainer
                                            sx={{
                                                maxHeight: 500,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <SortableHeader
                                                            label="Player"
                                                            column="Player"
                                                            activeColumn={careerBattingSort}
                                                            direction={careerBattingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    careerBattingSort,
                                                                    careerBattingDir,
                                                                    setCareerBattingSort,
                                                                    setCareerBattingDir,
                                                                )
                                                            }
                                                            sx={headerCell(theme)}
                                                        />
                                                        {BATTING_COLS.map((col) => (
                                                            <SortableHeader
                                                                key={col}
                                                                label={col}
                                                                column={col}
                                                                activeColumn={careerBattingSort}
                                                                direction={careerBattingDir}
                                                                onSort={(c) =>
                                                                    handleSort(
                                                                        c,
                                                                        careerBattingSort,
                                                                        careerBattingDir,
                                                                        setCareerBattingSort,
                                                                        setCareerBattingDir,
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {collegeCareerBattingRows.map((profile) => (
                                                        <TableRow key={`col-${profile.uuid}`} hover>
                                                            <TableCell sx={stickyCell(theme)}>
                                                                {getPlayerName(profile)}
                                                            </TableCell>
                                                            {BATTING_COLS.map((col) => (
                                                                <TableCell key={col} sx={statCell}>
                                                                    {renderHighlight(
                                                                        profile.college_scraped_data
                                                                            ?.career_totals
                                                                            ?.batting?.[col],
                                                                        col,
                                                                        careerBattingSort,
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                            {showCollege &&
                                hasCollegeData &&
                                collegeCareerPitchingRows.length > 0 && (
                                    <>
                                        {sectionHeader('College Career Pitching', '#8e24aa')}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {collegeCareerPitchingRows.length} player
                                                {collegeCareerPitchingRows.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                        <TableContainer
                                            sx={{
                                                maxHeight: 500,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <SortableHeader
                                                            label="Player"
                                                            column="Player"
                                                            activeColumn={careerPitchingSort}
                                                            direction={careerPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    careerPitchingSort,
                                                                    careerPitchingDir,
                                                                    setCareerPitchingSort,
                                                                    setCareerPitchingDir,
                                                                )
                                                            }
                                                            sx={headerCell(theme)}
                                                        />
                                                        {PITCHING_COLS.map((col) => (
                                                            <SortableHeader
                                                                key={col}
                                                                label={col}
                                                                column={col}
                                                                activeColumn={careerPitchingSort}
                                                                direction={careerPitchingDir}
                                                                onSort={(c) =>
                                                                    handleSort(
                                                                        c,
                                                                        careerPitchingSort,
                                                                        careerPitchingDir,
                                                                        setCareerPitchingSort,
                                                                        setCareerPitchingDir,
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {collegeCareerPitchingRows.map((profile) => (
                                                        <TableRow key={`col-${profile.uuid}`} hover>
                                                            <TableCell sx={stickyCell(theme)}>
                                                                {getPlayerName(profile)}
                                                            </TableCell>
                                                            {PITCHING_COLS.map((col) => (
                                                                <TableCell key={col} sx={statCell}>
                                                                    {renderHighlight(
                                                                        profile.college_scraped_data
                                                                            ?.career_totals
                                                                            ?.pitching?.[col],
                                                                        col,
                                                                        careerPitchingSort,
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                            {/* Summer League Career Totals */}
                            {showSummer && hasSummerData && summerCareerBattingRows.length > 0 && (
                                <>
                                    {sectionHeader('Summer League Career Batting', '#00897b')}
                                    <TableContainer
                                        sx={{
                                            maxHeight: 500,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <SortableHeader
                                                        label="Player"
                                                        column="Player"
                                                        activeColumn={careerBattingSort}
                                                        direction={careerBattingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                careerBattingSort,
                                                                careerBattingDir,
                                                                setCareerBattingSort,
                                                                setCareerBattingDir,
                                                            )
                                                        }
                                                        sx={headerCell(theme)}
                                                    />
                                                    {BATTING_COLS.map((col) => (
                                                        <SortableHeader
                                                            key={col}
                                                            label={col}
                                                            column={col}
                                                            activeColumn={careerBattingSort}
                                                            direction={careerBattingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    careerBattingSort,
                                                                    careerBattingDir,
                                                                    setCareerBattingSort,
                                                                    setCareerBattingDir,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summerCareerBattingRows.map((profile) => (
                                                    <TableRow key={`smr-${profile.uuid}`} hover>
                                                        <TableCell sx={stickyCell(theme)}>
                                                            {getPlayerName(profile)}
                                                        </TableCell>
                                                        {BATTING_COLS.map((col) => (
                                                            <TableCell key={col} sx={statCell}>
                                                                {renderHighlight(
                                                                    profile.summer_league_data
                                                                        ?.career_totals?.batting?.[
                                                                        col
                                                                    ],
                                                                    col,
                                                                    careerBattingSort,
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {showSummer && hasSummerData && summerCareerPitchingRows.length > 0 && (
                                <>
                                    {sectionHeader('Summer League Career Pitching', '#00897b')}
                                    <TableContainer
                                        sx={{
                                            maxHeight: 500,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <SortableHeader
                                                        label="Player"
                                                        column="Player"
                                                        activeColumn={careerPitchingSort}
                                                        direction={careerPitchingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                careerPitchingSort,
                                                                careerPitchingDir,
                                                                setCareerPitchingSort,
                                                                setCareerPitchingDir,
                                                            )
                                                        }
                                                        sx={headerCell(theme)}
                                                    />
                                                    {PITCHING_COLS.map((col) => (
                                                        <SortableHeader
                                                            key={col}
                                                            label={col}
                                                            column={col}
                                                            activeColumn={careerPitchingSort}
                                                            direction={careerPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    careerPitchingSort,
                                                                    careerPitchingDir,
                                                                    setCareerPitchingSort,
                                                                    setCareerPitchingDir,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summerCareerPitchingRows.map((profile) => (
                                                    <TableRow key={`smr-${profile.uuid}`} hover>
                                                        <TableCell sx={stickyCell(theme)}>
                                                            {getPlayerName(profile)}
                                                        </TableCell>
                                                        {PITCHING_COLS.map((col) => (
                                                            <TableCell key={col} sx={statCell}>
                                                                {renderHighlight(
                                                                    profile.summer_league_data
                                                                        ?.career_totals?.pitching?.[
                                                                        col
                                                                    ],
                                                                    col,
                                                                    careerPitchingSort,
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {/* High School Career Totals */}
                            {showHs &&
                                (hasCollegeData || hasSummerData) &&
                                careerBattingRows.length > 0 &&
                                sectionHeader('High School Career Batting', '#1565c0')}
                            {showHs && !hasCollegeData && !hasSummerData && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Career Batting Totals
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {careerBattingRows.length} player
                                        {careerBattingRows.length !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            )}
                            {showHs && careerBattingRows.length > 0 && (
                                <TableContainer
                                    sx={{
                                        maxHeight: 500,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <SortableHeader
                                                    label="Player"
                                                    column="Player"
                                                    activeColumn={careerBattingSort}
                                                    direction={careerBattingDir}
                                                    onSort={(c) =>
                                                        handleSort(
                                                            c,
                                                            careerBattingSort,
                                                            careerBattingDir,
                                                            setCareerBattingSort,
                                                            setCareerBattingDir,
                                                        )
                                                    }
                                                    sx={headerCell(theme)}
                                                />
                                                {BATTING_COLS.map((col) => (
                                                    <SortableHeader
                                                        key={col}
                                                        label={col}
                                                        column={col}
                                                        activeColumn={careerBattingSort}
                                                        direction={careerBattingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                careerBattingSort,
                                                                careerBattingDir,
                                                                setCareerBattingSort,
                                                                setCareerBattingDir,
                                                            )
                                                        }
                                                    />
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {careerBattingRows.map((profile) => (
                                                <TableRow key={profile.uuid} hover>
                                                    <TableCell sx={stickyCell(theme)}>
                                                        {getPlayerName(profile)}
                                                    </TableCell>
                                                    {BATTING_COLS.map((col) => (
                                                        <TableCell key={col} sx={statCell}>
                                                            {renderHighlight(
                                                                profile.scraped_data?.career_totals
                                                                    ?.batting?.[col],
                                                                col,
                                                                careerBattingSort,
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {showHs && careerPitchingRows.length > 0 && (
                                <>
                                    {hasCollegeData || hasSummerData ? (
                                        sectionHeader('High School Career Pitching', '#1565c0')
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mt: 3,
                                                mb: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Career Pitching Totals
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {careerPitchingRows.length} player
                                                {careerPitchingRows.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    )}
                                    <TableContainer
                                        sx={{
                                            maxHeight: 500,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <SortableHeader
                                                        label="Player"
                                                        column="Player"
                                                        activeColumn={careerPitchingSort}
                                                        direction={careerPitchingDir}
                                                        onSort={(c) =>
                                                            handleSort(
                                                                c,
                                                                careerPitchingSort,
                                                                careerPitchingDir,
                                                                setCareerPitchingSort,
                                                                setCareerPitchingDir,
                                                            )
                                                        }
                                                        sx={headerCell(theme)}
                                                    />
                                                    {PITCHING_COLS.map((col) => (
                                                        <SortableHeader
                                                            key={col}
                                                            label={col}
                                                            column={col}
                                                            activeColumn={careerPitchingSort}
                                                            direction={careerPitchingDir}
                                                            onSort={(c) =>
                                                                handleSort(
                                                                    c,
                                                                    careerPitchingSort,
                                                                    careerPitchingDir,
                                                                    setCareerPitchingSort,
                                                                    setCareerPitchingDir,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {careerPitchingRows.map((profile) => (
                                                    <TableRow key={profile.uuid} hover>
                                                        <TableCell sx={stickyCell(theme)}>
                                                            {getPlayerName(profile)}
                                                        </TableCell>
                                                        {PITCHING_COLS.map((col) => (
                                                            <TableCell key={col} sx={statCell}>
                                                                {renderHighlight(
                                                                    profile.scraped_data
                                                                        ?.career_totals?.pitching?.[
                                                                        col
                                                                    ],
                                                                    col,
                                                                    careerPitchingSort,
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Scouting */}
                {tab === 4 && (
                    <BaseballScoutingReport
                        profiles={profiles}
                        groupName={groupName}
                        groupLogo={groupLogo}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BaseballStatsViewer;
